import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { agentKindTag } from '../../chat-agent/agentKind';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ArtifactHistory, { Avatar, usageText, mdComponents } from './ArtifactHistory';
import CollectionView from './CollectionView';
import { SplitProvider } from '../../chat-agent/components/SplitBars';

/**
 * /artifact/:hash — a Kuutio-hosted, Kuutio-branded page for one published
 * result (specs/kuutio-agents/artifacts-microsite.md): the question and the
 * voices, the summary as the chat showed it, the full report, the history
 * turn by turn, and the sources. Light, Sitra-typed; public in v1.
 */
const Page = styled.div`
  min-height: 100vh;
  background: var(--color-grey-6, #f7f7f7);
  color: var(--color-grey-1, #292929);
  font-family: var(--font-sans, "RationalText", "Inter", sans-serif);
  font-size: 16px;
  line-height: 1.55;
  padding: 0 max(16px, env(safe-area-inset-left, 0px)) 64px max(16px, env(safe-area-inset-right, 0px));
`;
const Top = styled.header`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  max-width: 860px; margin: 0 auto; padding: 14px 0; border-bottom: 1px solid var(--color-grey-4, #d9d9d9);
  font-size: 13px; color: var(--color-grey-2, #636363);
  b { color: var(--color-grey-1, #292929); font-weight: 700; letter-spacing: .02em; }
`;
const Main = styled.main` max-width: 860px; margin: 0 auto; `;
const Eyebrow = styled.p` font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: var(--color-primary); margin: 28px 0 6px; `;
const H1 = styled.h1` font-size: clamp(24px, 3.6vw, 34px); line-height: 1.2; margin: 0 0 12px; font-weight: 700; text-wrap: balance; `;
const Synthetic = styled.span` margin-left: 5px; padding: 0 6px; border-radius: 999px; border: 1px solid var(--color-grey-3, #b5b5b5); font-size: 11px; line-height: 16px; `;
const Meta = styled.div` display: flex; flex-wrap: wrap; align-items: center; gap: 8px 18px; font-size: 13px; color: var(--color-grey-2, #636363); `;
const Avatars = styled.span` display: inline-flex; & > * + * { margin-left: -6px; } `;
const H2 = styled.h2` font-size: 22px; margin: 44px 0 8px; padding-top: 12px; border-top: 1px solid var(--color-grey-4, #d9d9d9); font-weight: 700; `;
const Lead = styled.p` margin: 0 0 12px; color: var(--color-grey-2, #636363); font-size: 14px; `;
const Card = styled.div`
  background: #fff; border: 1px solid var(--color-grey-4, #d9d9d9); border-radius: 10px; padding: 18px 22px;
  ${(p) => (p.accent ? 'border-left: 4px solid var(--color-primary);' : '')}
  h2 { font-size: 18px; margin: 20px 0 6px; border: 0; padding: 0; } h3 { font-size: 16px; margin: 16px 0 4px; }
  ul, ol { padding-left: 22px; } li { margin: 4px 0; } p { margin: 0 0 10px; }
  pre { white-space: pre-wrap; } a.ref { font-size: .85em; text-decoration: none; color: var(--color-primary); background: var(--color-blueberry-light); padding: 0 4px; border-radius: 4px; }
`;
const Sources = styled.ol`
  list-style: none; padding: 0; margin: 0; font-size: 14px;
  li { display: grid; grid-template-columns: 44px 1fr; gap: 8px; padding: 7px 0; border-bottom: 1px dashed var(--color-grey-4, #d9d9d9); }
  .n { color: var(--color-primary); font-variant-numeric: tabular-nums; }
  .type { font-size: 11.5px; color: var(--color-grey-2, #636363); text-transform: uppercase; letter-spacing: .04em; margin-left: 6px; }
  .ex { color: var(--color-grey-2, #636363); font-size: 13px; margin-top: 2px; }
`;
const Note = styled.p` font-size: 13px; color: var(--color-grey-2, #636363); border-top: 1px solid var(--color-grey-4, #d9d9d9); margin-top: 40px; padding-top: 12px; `;
const Center = styled.div` max-width: 640px; margin: 20vh auto; text-align: center; color: var(--color-grey-2, #636363); `;

const TYPE_LABEL = {
  kuutio_agent_research: 'tietopohja', kuutio_agent_pivot: 'vipupiste', kuutio_agent_solution: 'ratkaisu', kuutio_agent_lens_item: 'este/mahdollisuus',
  kuutio_agent_node: 'osallistujien idea', kuutio_agent_scenario: 'skenaario', kuutio_agent_perspective: 'näkökulma', kuutio_agent_organisation: 'organisaatio',
};

/** One number is one source across the whole answer: collect them from every message. */
function collectSources(snapshot) {
  const seen = new Map();
  const all = [...(snapshot.turns || []).flatMap((t) => t.sources || []), ...((snapshot.summary && snapshot.summary.sources) || [])];
  for (const s of all) {
    const num = s.num || Number(String(s.marker || '').replace(/\D/g, ''));
    if (num && !seen.has(num)) seen.set(num, s);
  }
  return [...seen.entries()].sort((a, b) => a[0] - b[0]);
}

export default function ArtifactScreen() {
  const { hash } = useParams();
  const { t, language } = useTranslation();
  const [state, setState] = useState({ loading: true, error: null, artifact: null });

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.ARTIFACT_GET(hash))
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) setState({ loading: false, error: { status: r.status, ...body }, artifact: null });
        else setState({ loading: false, error: null, artifact: body });
      })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: { status: 0, error: e.message }, artifact: null }); });
    return () => { cancelled = true; };
  }, [hash]);

  useEffect(() => {
    if (state.artifact?.title) document.title = `${state.artifact.title} — Kuutio`;
  }, [state.artifact]);

  if (state.loading) return <Page><Center>{t('artifact.loading')}</Center></Page>;
  if (state.error) {
    const key = state.error.status === 410 ? 'artifact.revoked' : state.error.status === 401 ? 'artifact.needsSignIn' : state.error.status === 404 ? 'artifact.notFound' : 'artifact.failed';
    return <Page><Center><h1 style={{ fontSize: 22 }}>{t(key)}</h1><p>{state.error.error}</p></Center></Page>;
  }

  const a = state.artifact;
  const s = a.snapshot || {};
  if (a.kind === 'collection') {
    return (
      <Page>
        <Top><span><b>KUUTIO</b> · Sitra</span><span>{s.instance?.title || ''}</span></Top>
        <Main>
          <Eyebrow>{t('artifact.eyebrow')} · {t('artifact.collection')}</Eyebrow>
          <H1>{s.title || a.title}</H1>
          <SplitProvider tone="light"><CollectionView snapshot={s} /></SplitProvider>
          <Note>{t('artifact.disclaimer')} · {t('artifact.published', { date: new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI', { dateStyle: 'medium' }).format(new Date(a.createdAt)) })}</Note>
        </Main>
      </Page>
    );
  }
  const th = s.thread || {};
  const voices = th.participants || [];
  const kindLabel = t(`chatAgent.thread.kind.${th.kind || 'bounce'}`);
  const sources = collectSources(s);
  const dateLocale = language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI';
  const when = (ts) => (ts ? new Intl.DateTimeFormat(dateLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ts)) : '');

  return (
    <Page>
      <Top><span><b>KUUTIO</b> · Sitra</span><span>{s.instance?.title || ''}</span></Top>
      <Main>
       <SplitProvider sources={sources.map(([, src]) => src)} tone="light">
        <Eyebrow>{t('artifact.eyebrow')} · {kindLabel}</Eyebrow>
        <H1>{th.question || s.question?.text || a.title}</H1>
        <Meta>
          <Avatars>{voices.map((v) => <Avatar key={v.slug} slug={v.slug} size={28} />)}</Avatars>
          <span>{voices.map((v, i) => <React.Fragment key={v.slug}>{i > 0 ? ' · ' : ''}{String(v.slug).toUpperCase()} {v.name}{agentKindTag(v.kind) && <Synthetic>{t(agentKindTag(v.kind))}</Synthetic>}</React.Fragment>)}</span>
          <span>{when(th.createdAt || a.createdAt)}</span>
          {th.usage && <span>{usageText(th.usage, t, language)}</span>}
        </Meta>

        <H2 id="yhteenveto">1. {t('artifact.summary')}</H2>
        <Lead>{t('artifact.summaryLead')}</Lead>
        <Card accent>
          {s.summary?.text
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{s.summary.text}</ReactMarkdown>
            : <p style={{ color: 'var(--color-grey-2)' }}>{t('artifact.noSummary')}</p>}
        </Card>

        <H2 id="raportti">2. {t('artifact.report')}</H2>
        <Lead>{t('artifact.reportLead')}</Lead>
        <Card>
          {th.report
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{th.report}</ReactMarkdown>
            : <p style={{ color: 'var(--color-grey-2)' }}>{t('chatAgent.thread.noReport')}</p>}
        </Card>

        <H2 id="historia">3. {t('artifact.history')}</H2>
        <Lead>{t('artifact.historyLead')}</Lead>
        <ArtifactHistory snapshot={s} when={when} />

        <H2 id="lahteet">4. {t('artifact.sources')}</H2>
        <Lead>{t('artifact.sourcesLead')}</Lead>
        <Sources>
          {sources.map(([n, src]) => (
            <li key={n} id={`src-${n}`}>
              <span className="n">[{n}]</span>
              <div>
                {src.url ? <a href={src.url} target="_blank" rel="noreferrer">{src.citation}</a> : src.citation}
                <span className="type">{TYPE_LABEL[src.type] || src.type || ''}</span>
                {src.excerpt && <div className="ex">{src.excerpt}</div>}
              </div>
            </li>
          ))}
        </Sources>
        <Note>{t('artifact.disclaimer')} · {t('artifact.published', { date: when(a.createdAt) })}</Note>
       </SplitProvider>
      </Main>
    </Page>
  );
}

